import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Table,
    Space,
    Typography,
    Row,
    Col,
    Spin,
    Alert,
    Empty,
    Tag,
    Divider,
    Popconfirm,
    message,
    Badge,
    Avatar,
    List,
    Tabs,
    Progress,
    Tooltip,
    Statistic
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    EyeOutlined,
    UserOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FilterOutlined,
    ReloadOutlined,
    BarChartOutlined,
    PoundCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import { useGetUserData } from '../../../hooks/useGetUserData';
import { IoRefresh, IoRefreshCircle, IoRefreshCircleOutline } from 'react-icons/io5';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Usage Tab Component
const UsageTab = () => {
    const [usageData, setUsageData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        PageNumber: 1,
        PageSize: 12,
        PackageId: '',
        UserId: '',
        IsExpired: null
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const [form] = Form.useForm();

    useEffect(() => {
        loadUsageData();
    }, [filters]);

    const loadUsageData = async () => {
        setLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    params.append(key, value.toString());
                }
            });

            const response = await drEasyApi.get(`/api/Packages/GetUsage?${params.toString()}`);
            
            setUsageData(response.data.items || []);
            setPagination(prev => ({
                ...prev,
                current: response.data.pageNumber || 1,
                pageSize: response.data.pageSize || 12,
                total: response.data.totalCount || 0
            }));
        } catch (error) {
            console.error('Error loading usage data:', error);
            if (error.response?.status === 403) {
                window.location.href = "/sub";
            } else {
                message.error('Failed to load usage data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterSubmit = (values) => {
        setFilters(prev => ({
            ...prev,
            PageNumber: 1,
            PackageId: values.PackageId || '',
            UserId: values.UserId || '',
            IsExpired: values.IsExpired
        }));
    };

    const handleClearFilters = () => {
        form.resetFields();
        setFilters({
            PageNumber: 1,
            PageSize: 12,
            PackageId: '',
            UserId: '',
            IsExpired: null
        });
    };

    const handlePaginationChange = (page, pageSize) => {
        setFilters(prev => ({
            ...prev,
            PageNumber: page,
            PageSize: pageSize
        }));
    };

    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setIsDetailsModalOpen(true);
    };

    const closeDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedUser(null);
    };

    const renderServiceProgress = (service) => {
        const percentage = service.totalQuantity > 0 ? 
            Math.round((service.usedQuantity / service.totalQuantity) * 100) : 0;
        
        let strokeColor = '#52c41a';
        if (percentage >= 90) strokeColor = '#ff4d4f';
        else if (percentage >= 70) strokeColor = '#faad14';
        
        return (
            <div key={service.serviceId} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <Text strong>{service.serviceName}</Text>
                    <Text type="secondary">
                        {service.usedQuantity}/{service.totalQuantity}
                    </Text>
                </div>
                <Progress
                    percent={percentage}
                    strokeColor={strokeColor}
                    size="small"
                    status={percentage === 100 ? 'success' : 'normal'}
                />
            </div>
        );
    };

    const renderUserCard = (user) => {
        const active = user.packages.filter(pkg => !pkg.isExpired).length;
        const expired = user.packages.filter(pkg => pkg.isExpired).length;
        const totalServices = user.packages.reduce((sum, pkg) => sum + pkg.services.length, 0);
        
        let totalUsed = 0;
        let totalAvailable = 0;
        
        user.packages.forEach(pkg => {
            pkg.services.forEach(service => {
                totalUsed += service.usedQuantity;
                totalAvailable += service.totalQuantity;
            });
        });
        
        const usagePercentage = totalAvailable > 0 ? Math.round((totalUsed / totalAvailable) * 100) : 0;

        return (
            <Card
                key={user.userId}
                hoverable
                className="border border-primary/50 hover:border-primary/40 transition-all duration-300"
                actions={[
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(user)}
                        className="text-primary"
                    >
                        View Details
                    </Button>
                ]}
                style={{ height: '100%' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <Avatar
                        src={user.userProfile ? getImg(user.userProfile) : null}
                        icon={<UserOutlined />}
                        size={48}
                        style={{ marginRight: '12px' }}
                    />
                    <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0 }}>{user.userName}</Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>ID: {user.userId}</Text>
                    </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <Row gutter={[16, 8]} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                            <Badge count={user.packages.length} style={{ backgroundColor: '#298E9F' }}>
                            </Badge>
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Total Packages</div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                            <Badge count={totalServices} style={{ backgroundColor: '#FEBA17' }}>
                            </Badge>
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Total Services</div>
                        </div>
                    </Col>
                </Row>

                <Row gutter={8} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <Tag color="green" style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                            {active} Active
                        </Tag>
                    </Col>
                    <Col span={12}>
                        <Tag color="red" style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                            {expired} Expired
                        </Tag>
                    </Col>
                </Row>

                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text strong>Usage Summary</Text>
                        <Text type="secondary">{totalUsed}/{totalAvailable}</Text>
                    </div>
                    <Progress
                        percent={usagePercentage}
                        size="small"
                        strokeColor={usagePercentage >= 80 ? '#ff4d4f' : '#1890ff'}
                    />
                </div>
            </Card>
        );
    };

    return (
        <div>
            {/* Filters */}
            <Card title={<><FilterOutlined /> Filters</>} style={{ marginBottom: '24px' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFilterSubmit}
                    initialValues={filters}
                >
                    <Row gutter={16}>
                      
                        <Col xs={24} sm={8} md={6}>
                            <Form.Item label="Status" name="IsExpired">
                                <Select placeholder="Select Status" allowClear>
                                    <Option value={false}>Active</Option>
                                    <Option value={true}>Expired</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item style={{ marginTop: '30px' }}>
                                <Space>
                                    <Button type="primary" htmlType="submit" icon={<FilterOutlined />} loading={loading} className="bg-primary">
                                        Apply Filters
                                    </Button>
                                    <Button onClick={handleClearFilters}>
                                        <IoRefresh className='text-lg text-primary'/>
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Usage Data Cards */}
            {loading ? (
                <Row gutter={[16, 16]}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={i}>
                            <Card loading={true}>
                                <Card.Meta title="Loading..." description="Please wait..." />
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <>
                    <Row gutter={[16, 16]}>
                        {usageData.map(user => (
                            <Col xs={24} sm={12} md={8} lg={6} key={user.userId}>
                                {renderUserCard(user)}
                            </Col>
                        ))}
                    </Row>

                    {/* Pagination */}
                    {pagination.total > 0 && (
                        <Row justify="center" style={{ marginTop: '24px' }}>
                            <Card>
                                <div style={{ padding: '8px 0' }}>
                                    <Text style={{ marginRight: '16px' }}>
                                        Showing {((pagination.current - 1) * pagination.pageSize) + 1} to{' '}
                                        {Math.min(pagination.current * pagination.pageSize, pagination.total)} of{' '}
                                        {pagination.total} users
                                    </Text>
                                    <Button
                                        disabled={pagination.current === 1}
                                        onClick={() => handlePaginationChange(pagination.current - 1, pagination.pageSize)}
                                        style={{ marginRight: '8px' }}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                                        onClick={() => handlePaginationChange(pagination.current + 1, pagination.pageSize)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Card>
                        </Row>
                    )}
                </>
            )}

            {/* Empty State */}
            {!loading && usageData.length === 0 && (
                <Card>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No usage data found"
                    >
                        <Button type="primary" onClick={loadUsageData} className="bg-primary">
                            Refresh Data
                        </Button>
                    </Empty>
                </Card>
            )}

            {/* User Details Modal */}
            <Modal
                title={
                    selectedUser ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Avatar
                                src={selectedUser.userProfile ? getImg(selectedUser.userProfile) : null}
                                icon={<UserOutlined />}
                                size={40}
                            />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <Text strong style={{ fontSize: '16px' }}>{selectedUser.userName}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>User ID: {selectedUser.userId}</Text>
                            </div>
                        </div>
                    ) : "User Details"
                }
                open={isDetailsModalOpen}
                onCancel={closeDetailsModal}
                footer={[
                    <Button key="close" onClick={closeDetailsModal}>
                        Close
                    </Button>
                ]}
                width="90%"
                style={{ maxWidth: '1200px' }}
                bodyStyle={{ 
                    maxHeight: '70vh', 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '16px'
                }}
            >
                {selectedUser && (
                    <div>
                        {/* User Summary */}
                        <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
                            <Col xs={12} sm={6}>
                                <Card size="small" className='border-orange-300'>
                                    <Statistic
                                        title="Total Packages"
                                        value={selectedUser.packages.length}
                                        prefix={<AppstoreOutlined />}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small " className='border-green-300'>
                                    <Statistic
                                        title="Active"
                                        value={selectedUser.packages.filter(pkg => !pkg.isExpired).length}
                                        prefix={<CheckCircleOutlined />}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small" className='border-red-300'>
                                    <Statistic
                                        title="Expired"
                                        value={selectedUser.packages.filter(pkg => pkg.isExpired).length}
                                        prefix={<ExclamationCircleOutlined />}
                                        valueStyle={{ color: '#ff4d4f' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small" className='border-primary/50'>
                                    <Statistic
                                        title="Total Services"
                                        value={selectedUser.packages.reduce((sum, pkg) => sum + pkg.services.length, 0)}
                                        prefix={<AppstoreOutlined />}
                                        valueStyle={{ color: '#298E9F' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Divider />

                        {/* Package Details */}
                        <Title level={4} style={{ marginBottom: '16px' }}>Package Details</Title>
                        <Row gutter={[12, 16]}>
                            {selectedUser.packages.map((pkg) => (
                                <Col xs={24} md={12} key={pkg.packageId}>
                                    <Card
                                        size="small"
                                        title={
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between',
                                                minWidth: 0
                                            }}>
                                                <Text strong style={{ 
                                                    fontSize: '14px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    marginRight: '8px'
                                                }}>
                                                    {pkg.title}
                                                </Text>
                                                {pkg.isExpired ? (
                                                    <Tag color="red">Expired</Tag>
                                                ) : (
                                                    <Tag color="green">Active</Tag>
                                                )}
                                            </div>
                                        }
                                        className={pkg.isExpired ? 'border border-red-200' : 'border border-green-200'}
                                        style={{ 
                                            height: 'fit-content',
                                            minWidth: 0
                                        }}
                                    >
                                        <div style={{ marginBottom: '12px' }}>
                                            <Text type="secondary" style={{ 
                                                fontSize: '12px', 
                                                display: 'block',
                                                wordBreak: 'break-word'
                                            }}>
                                                <CalendarOutlined /> Purchased: {new Date(pkg.purchaseDate).toLocaleDateString()}
                                            </Text>
                                            <Text type="secondary" style={{ 
                                                fontSize: '12px', 
                                                display: 'block',
                                                wordBreak: 'break-word'
                                            }}>
                                                <ClockCircleOutlined /> Expires: {new Date(pkg.expiryDate).toLocaleDateString()}
                                            </Text>
                                        </div>
                                        
                                        <Divider style={{ margin: '12px 0' }} />
                                        
                                        <div>
                                            <div style={{ minWidth: 0 }}>
                                                {pkg.services.map(service => renderServiceProgress(service))}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// Main Packages Management Component
const PackagesManagement = () => {
    const [packages, setPackages] = useState([]);
    const [services, setServices] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
    const [selectedPackageServices, setSelectedPackageServices] = useState([]);
    const [editingPackage, setEditingPackage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('packages');
    const [form] = Form.useForm();
    const { data: user } = useGetUserData();

    useEffect(() => {
        if (activeTab === 'packages') {
            loadData();
        }
    }, [activeTab]);

    const loadData = async () => {
        setDataLoading(true);

        try {
            // Fetch packages and services in parallel
            const [packagesResponse, servicesResponse] = await Promise.all([
                drEasyApi.get('/api/Packages/GetMyPackages'),
                drEasyApi.get('/api/Services/GetMyServices')
            ]);

            // Normalize packages: use services directly from API and add display fields
            const normalizedPackages = (packagesResponse.data || []).map(pkg => ({
                ...pkg,
                // Use titleEn for display (fallback to titleAr if titleEn is empty)
                title: pkg.titleEn || pkg.titleAr || 'Untitled Package',
                description: pkg.descriptionEn || pkg.descriptionAr || '',
                items: pkg.services || [] // Keep services as items for internal use
            }));

            setPackages(normalizedPackages);
            setServices(servicesResponse.data || []);
        } catch (err) {
             if (err.response?.status === 403) {
                window.location.href = "/sub";
            }
        } finally {
            setDataLoading(false);
        }
    };

    const openModal = (packageData = null) => {
        if (packageData) {
            setEditingPackage(packageData);
            form.setFieldsValue({
                id: packageData.id,
                titleEn: packageData.titleEn || '',
                titleAr: packageData.titleAr || '',
                descriptionEn: packageData.descriptionEn || '',
                descriptionAr: packageData.descriptionAr || '',
                price: packageData.price,
                validityInDays: packageData.validityInDays,
                items: packageData.items && packageData.items.length > 0 ? packageData.items : [{ serviceId: undefined, quantity: 1 }]
            });
        } else {
            setEditingPackage(null);
            // Initialize with one empty service item
            form.setFieldsValue({
                titleEn: '',
                titleAr: '',
                descriptionEn: '',
                descriptionAr: '',
                items: [{ serviceId: undefined, quantity: 1 }]
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingPackage(null);
        form.resetFields();
    };

    const openServicesModal = (packageServices) => {
        const servicesWithDetails = packageServices.map(item => {
            const service = services.find(s => s.id === item.serviceId);
            return {
                ...item,
                serviceName: service?.title || 'Unknown Service',
                servicePrice: service?.price || 0,
                servicePhoto: service?.photoID || null
            };
        });
        setSelectedPackageServices(servicesWithDetails);
        setIsServicesModalOpen(true);
    };

    const closeServicesModal = () => {
        setIsServicesModalOpen(false);
        setSelectedPackageServices([]);
    };

    // Custom validation function for package services
    const validatePackageServices = (items) => {
        if (!items || items.length === 0) {
            return 'At least one service is required';
        }

        // Filter out items with valid serviceId
        const validItems = items.filter(item => item && item.serviceId);
        
        if (validItems.length === 0) {
            return 'Please select at least one service';
        }

        // Check if any item has invalid quantity
        const hasInvalidQuantity = validItems.some(item => !item.quantity || item.quantity < 1);
        if (hasInvalidQuantity) {
            return 'All services must have a quantity of at least 1';
        }

        // Rule: Must have either 1 service with quantity > 1 OR at least 2 services
        if (validItems.length === 1) {
            if (validItems[0].quantity <= 1) {
                return 'A single service package must have quantity greater than 1, or add at least 2 different services';
            }
        }

        return null; // Valid
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        try {
            if (!user?.id) {
                message.error('User information not available. Please refresh and try again.');
                setLoading(false);
                return;
            }

            // Enhanced validation with custom rules
            const validationError = validatePackageServices(values.items);
            if (validationError) {
                message.error(validationError);
                setLoading(false);
                return;
            }

            if (editingPackage) {
                // PUT /api/Packages/Edit
                const updateData = {
                    id: editingPackage.id,
                    titleEn: values.titleEn || "",
                    titleAr: values.titleAr || "",
                    descriptionEn: values.descriptionEn || "",
                    descriptionAr: values.descriptionAr || "",
                    price: Number(values.price) || 0,
                    validityInDays: values.validityInDays ? Number(values.validityInDays) : 0,
                    items: (values.items || []).map(item => ({
                        serviceId: item.serviceId,
                        quantity: Number(item.quantity) || 1
                    }))
                };

                console.log('Update data:', updateData);

                await drEasyApi.put('/api/Packages/Edit', updateData);
                await loadData();
                message.success('Package updated successfully!');
            } else {
                // POST /api/Packages/Add
                const createData = {
                    titleEn: values.titleEn || "",
                    titleAr: values.titleAr || "",
                    descriptionEn: values.descriptionEn || "",
                    descriptionAr: values.descriptionAr || "",
                    price: Number(values.price) || 0,
                    validityInDays: values.validityInDays ? Number(values.validityInDays) : 0,
                    items: (values.items || []).map(item => ({
                        serviceId: item.serviceId,
                        quantity: Number(item.quantity) || 1
                    }))
                };

                console.log('Create data:', createData);

                const response = await drEasyApi.post('/api/Packages/Add', createData);
                await loadData();
                message.success('Package created successfully!');
            }

            closeModal();
        } catch (error) {
            console.error('Error saving package:', error);
            
            // Enhanced error logging
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response headers:', error.response.headers);
                
                // Log validation errors if present
                if (error.response.data?.errors) {
                    console.error('Validation errors:', error.response.data.errors);
                }
            }
            
            // More specific error messages
            let errorMessage = 'Failed to save package. Please try again.';
            if (error.response?.status === 400) {
                if (error.response.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorFields = Object.keys(errors);
                    if (errorFields.length > 0) {
                        errorMessage = `Validation error: ${errors[errorFields[0]][0]}`;
                    } else {
                        errorMessage = 'Invalid data provided. Please check all required fields.';
                    }
                } else {
                    errorMessage = 'Invalid data provided. Please check all required fields.';
                }
            } else if (error.response?.status === 401) {
                errorMessage = 'Authentication failed. Please login again.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to perform this action.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (packageId) => {
        setLoading(true);

        try {
            await drEasyApi.delete(`/api/Packages/Delete?Id=${packageId}`);
            await loadData();
            message.success('Package deleted successfully!');
        } catch (error) {
            console.error('Error deleting package:', error);
            message.error(error.response?.data?.message || 'Failed to delete package. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderPackagesTab = () => (
        <div>
            {/* Header */}
            <Card style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <AppstoreOutlined />
                            My Packages
                        </Title>
                        <Text type="secondary">Manage your service packages and pricing</Text>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={() => openModal()}
                            loading={dataLoading}
                            className='bg-primary'
                        >
                            Add New Package
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Loading State */}
            {dataLoading ? (
                <Row gutter={[16, 16]}>
                    {[1, 2, 3].map((i) => (
                        <Col xs={24} sm={12} lg={8} key={i}>
                            <Card loading={true}>
                                <Card.Meta title="Loading..." description="Please wait..." />
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <>
                    {/* Packages Grid */}
                    <Row gutter={[16, 16]}>
                        {packages.map((pkg) => (
                            <Col xs={24} sm={12} lg={8} key={pkg.id}>
                                <Card className='border border-primary/80 border-1'
                                    title={pkg.titleEn || pkg.titleAr || 'Untitled Package'}
                                    extra={
                                        <Space>
                                            <Button
                                                type="text"
                                                icon={<EditOutlined />}
                                                onClick={() => openModal(pkg)}
                                                loading={loading}
                                            />
                                            <Popconfirm
                                                title="Delete Package"
                                                description="Are you sure you want to delete this package?"
                                                onConfirm={() => handleDelete(pkg.id)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    loading={loading}
                                                />
                                            </Popconfirm>
                                        </Space>
                                    }
                                    actions={[
                                        <div key="price" style={{ textAlign: 'center' }}>
                                            <PoundCircleOutlined style={{ color: '#464545ff', marginRight: '4px' }} />
                                            <Text strong style={{ color: '#52c41a' }}>EGP {pkg.price}</Text>
                                        </div>,
                                        pkg.validityInDays ? (
                                            <div key="validity" style={{ textAlign: 'center' }}>
                                                <CalendarOutlined style={{ color: '#464545ff', marginRight: '4px' }} />
                                                <Text>{pkg.validityInDays} days</Text>
                                            </div>
                                        ) : (
                                            <div key="validity" style={{ textAlign: 'center' }}>
                                                <CalendarOutlined style={{ color: '#999', marginRight: '4px' }} />
                                                <Text type="secondary">No expiry</Text>
                                            </div>
                                        ),
                                        <div key="services" style={{ textAlign: 'center' }}>
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<AppstoreOutlined style={{ color: '#464545ff' }} />}
                                                onClick={() => openServicesModal(pkg.items || [])}
                                                style={{ padding: 0, height: 'auto' }}
                                            >
                                                <Text style={{ color: '#464545ff' }}>
                                                    {pkg.items?.length || 0} services
                                                </Text>
                                            </Button>
                                        </div>
                                    ]}
                                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <Paragraph className='text-gray-600' ellipsis={{ rows: 2 }}>
                                            {pkg.descriptionEn || pkg.descriptionAr || 'No description'}
                                        </Paragraph>

                                        {pkg.items && pkg.items.length > 0 && (
                                            <>
                                                <Divider style={{ margin: '12px 0' }} />
                                                <div>
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'space-between',
                                                        marginBottom: '8px'
                                                    }}>
                                                      
                                                        <Button
                                                            type="link"
                                                            className='text-primary font-base'
                                                            size="small"
                                                            icon={<EyeOutlined />}
                                                            onClick={() => openServicesModal(pkg.items || [])}
                                                            style={{ 
                                                                padding: '0 4px', 
                                                                height: 'auto',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            View Services
                                                        </Button>
                                                    </div>
                                                  
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Empty State */}
                    {packages.length === 0 && (
                        <Card>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No packages found"
                            >
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className='bg-primary'>
                                    Create Package
                                </Button>
                            </Empty>
                        </Card>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div style={{ padding: '20px', minHeight: '100vh' }}>
            <div >
                {/* Main Header */}
                <Card style={{ marginBottom: '24px' }}>
                    <div  >
                            <h1 className='text-center text-primary_hover text-3xl font-semibold' >
                                Packages Management
                            </h1>
                     
                    </div>
                </Card>

                {/* Tabs */}
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    size="large"
                    style={{ marginBottom: '24px' }}
                >
                    <TabPane 
                        tab={
                            <span>
                                <AppstoreOutlined />
                                My Packages
                            </span>
                        } 
                        key="packages"
                    >
                        {renderPackagesTab()}
                    </TabPane>
                    <TabPane 
                        tab={
                            <span>
                                <BarChartOutlined />
                                Usage Analytics
                            </span>
                        } 
                        key="usage"
                    >
                        <UsageTab />
                    </TabPane>
                </Tabs>
            </div>

            {/* Package Form Modal */}
            <Modal
                title={editingPackage ? 'Edit Package' : 'Add New Package'}
                open={isModalOpen}
                onCancel={closeModal}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ items: [{ serviceId: undefined, quantity: 1 }] }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Package Title (English)"
                                name="titleEn"
                                rules={[
                                    { required: true, message: 'Please enter package title in English' },
                                    { min: 3, message: 'Title must be at least 3 characters' }
                                ]}
                            >
                                <Input placeholder="Enter package title in English" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Package Title (Arabic)"
                                name="titleAr"
                                rules={[
                                    { required: true, message: 'Please enter package title in Arabic' },
                                    { min: 3, message: 'Title must be at least 3 characters' }
                                ]}
                            >
                                <Input placeholder="أدخل عنوان الباقة بالعربية" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Price (EGP)"
                                name="price"
                                rules={[
                                    { required: true, message: 'Please enter price' },
                                    { type: 'number', min: 0.01, message: 'Price must be greater than 0' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    min={0}
                                    step={0.01}
                                    prefix={<PoundCircleOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span>
                                        Validity (Days) 
                                        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                            (Optional - Leave empty for no expiry)
                                        </Text>
                                    </span>
                                }
                                name="validityInDays"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Leave empty for no expiry"
                                    min={1}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Description (English)"
                        name="descriptionEn"
                        rules={[
                            { max: 500, message: 'Description cannot exceed 500 characters' }
                        ]}
                    >
                        <TextArea
                            placeholder="Enter package description in English"
                            rows={3}
                            showCount
                            maxLength={500}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="Description (Arabic)"
                        name="descriptionAr"
                        rules={[
                            { max: 500, message: 'Description cannot exceed 500 characters' }
                        ]}
                    >
                        <TextArea
                            placeholder="أدخل وصف الباقة بالعربية"
                            rows={3}
                            showCount
                            maxLength={500}
                        />
                    </Form.Item>

                    <Form.Item 
                        label={
                            <span className='text-lg font-bold'>
                                Included Services 
                                <Text type="danger" style={{ marginLeft: '4px' }}>*</Text>
                            </span>
                        }
                        required
                    >
                        <Form.List 
                            name="items"
                            rules={[
                                {
                                    validator: async (_, items) => {
                                        const error = validatePackageServices(items);
                                        if (error) {
                                            return Promise.reject(new Error(error));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Card key={field.key} size="small" style={{ marginBottom: 16 }} >
                                            <Row gutter={16} align="middle">
                                                <Col span={12}>
                                                    <Form.Item
                                                        {...field}
                                                        label="Service"
                                                        name={[field.name, 'serviceId']}
                                                        rules={[{ required: true, message: 'Please select a service' }]}
                                                        style={{ margin: 0 }}
                                                    >
                                                        <Select placeholder="Select service">
                                                            {services.map(service => (
                                                                <Option key={service.id} value={service.id}>
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <img 
                                                                            src={getImg(service.photoID)} 
                                                                            style={{ width: 24, height: 24, borderRadius: 4 }} 
                                                                            alt={service.title}
                                                                        />
                                                                        {service.title} - EGP {service.price}
                                                                    </span>
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Quantity"
                                                        {...field}
                                                        name={[field.name, 'quantity']}
                                                        rules={[
                                                            { required: true, message: 'Required' },
                                                            { type: 'number', min: 1, message: 'Min 1' }
                                                        ]}
                                                        style={{ margin: 0 }}
                                                    >
                                                        <InputNumber 
                                                            min={1} 
                                                            placeholder="Quantity" 
                                                            style={{ width: '100%' }} 
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={4}>
                                                    {fields.length > 1 && (
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => remove(field.name)}
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}
                                    
                                    <Button
                                        type="dashed"
                                        onClick={() => add({ serviceId: undefined, quantity: 1 })}
                                        block
                                        icon={<PlusOutlined />}
                                        style={{ marginTop: 16 }}
                                    >
                                        Add Another Service
                                    </Button>
                                    
                                    <Form.ErrorList errors={errors} />
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Row justify="end" gutter={8} style={{ marginTop: '24px' }}>
                        <Col>
                            <Button onClick={closeModal}>
                                Cancel
                            </Button>
                        </Col>
                        <Col>
                            <Button type="primary" htmlType="submit" loading={loading} className='bg-primary'>
                                {editingPackage ? 'Update Package' : 'Create Package'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Services Details Modal */}
            <Modal
                title="Package Services"
                open={isServicesModalOpen}
                onCancel={closeServicesModal}
                footer={[
                    <Button key="close" onClick={closeServicesModal}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedPackageServices.length > 0 ? (
                    <List
                        itemLayout="horizontal"
                        dataSource={selectedPackageServices}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Text key="quantity" strong style={{ color: '#1890ff' }}>
                                        ×{item.quantity}
                                    </Text>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        item.servicePhoto ? (
                                            <Avatar 
                                                src={getImg(item.servicePhoto)} 
                                                size={48} 
                                                shape="square"
                                                style={{ borderRadius: '8px' }}
                                            />
                                        ) : (
                                            <Avatar 
                                                size={48} 
                                                shape="square"
                                                style={{ 
                                                    borderRadius: '8px',
                                                    backgroundColor: '#f0f0f0',
                                                    color: '#999'
                                                }}
                                            >
                                                {item.serviceName?.charAt(0) || '?'}
                                            </Avatar>
                                        )
                                    }
                               
                                    description={
                                         <Text type="secondary">
                                         {item.servicePrice?  `Total: EGP ${(item.servicePrice * item.quantity).toFixed(2)}`: 'Total: EGP 0.00'}
                                        </Text>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty description="No services in this package" />
                )}
            </Modal>
        </div>
    );
};

export default PackagesManagement;